import { useEffect, useMemo, useState } from 'react';
import { type SortingState } from '@tanstack/react-table';
import Input from '../common/Input';
import Table from '../common/Table';
import { createConsolidatedColumns } from './dashboardColumns';
import { ConsolidatedRecord } from '../types';
import { useConsolidatedTransactionsQuery } from '../hooks/useConsolidatedTransactions';

const allStatusOptions = ['ALL', 'DRAFT', 'PAID', 'UNPAID', 'CANCELED'] as const;

function AllTab() {
  const [allSearchTerm, setAllSearchTerm] = useState('');
  const [debouncedAllSearchTerm, setDebouncedAllSearchTerm] = useState('');
  const [allStatusFilter, setAllStatusFilter] = useState('ALL');
  const [allSorting, setAllSorting] = useState<SortingState>([]);
  const [allPageIndex, setAllPageIndex] = useState(0);
  const [allPageSize, setAllPageSize] = useState(10);

  const activeSort = allSorting[0];
  const rawAllSortField = allSorting[0]?.id ? String(allSorting[0].id) : '';
  const allSortField = rawAllSortField === 'name' ? 'firstName' : rawAllSortField;
  const allSortDirection: 'asc' | 'desc' = activeSort?.desc ? 'desc' : 'asc';
  const consolidatedQuery = useConsolidatedTransactionsQuery(
    true,
    allPageIndex + 1,
    allPageSize,
    allStatusFilter,
    allSortField,
    allSortDirection,
    debouncedAllSearchTerm
  );
  const consolidatedMeta = consolidatedQuery.data?.meta;
  const consolidatedColumns = useMemo(() => createConsolidatedColumns(), []);

  const filteredConsolidatedRecords = useMemo<ConsolidatedRecord[]>(() => consolidatedQuery.data?.data ?? [], [consolidatedQuery.data?.data]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedAllSearchTerm(allSearchTerm);
    }, 1000);

    return () => window.clearTimeout(timer);
  }, [allSearchTerm]);

  useEffect(() => {
    setAllPageIndex(0);
  }, [allStatusFilter, debouncedAllSearchTerm]);

  useEffect(() => {
    setAllPageIndex(0);
  }, [allSortField, allSortDirection]);

  const consolidatedErrorMessage = (consolidatedQuery.error as Error | null)?.message ?? '';

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <Table<ConsolidatedRecord>
        data={filteredConsolidatedRecords}
        columns={consolidatedColumns}
        topHeader={
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="max-w-lg flex-1">
              <Input
                value={allSearchTerm}
                onChange={(event) => setAllSearchTerm(event.target.value)}
                placeholder="Search by name, email, phone, invoice ID, or transaction"
                aria-label="Search all records"
              />
            </div>
            <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Invoice Status
              <select
                value={allStatusFilter}
                onChange={(event) => setAllStatusFilter(event.target.value)}
                className="min-w-[170px] rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
                aria-label="Filter all records by invoice status"
              >
                {allStatusOptions.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </label>
          </div>
        }
        loading={consolidatedQuery.isFetching}
        enableSorting
        sorting={allSorting}
        onSortingChange={setAllSorting}
        pagination={{
          pageIndex: allPageIndex,
          pageSize: allPageSize,
          totalCount: consolidatedMeta?.total ?? filteredConsolidatedRecords.length,
          totalPages: consolidatedMeta?.totalPages,
          onPageChange: setAllPageIndex,
          onPageSizeChange: setAllPageSize,
          pageSizeOptions: [10, 20, 40, 80],
        }}
      />
      {consolidatedErrorMessage && (
        <p className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{consolidatedErrorMessage}</p>
      )}
    </div>
  );
}

export default AllTab;
